import React, { type ReactNode } from 'react';
import styled from 'types/theme.types';
import { css, useTheme } from '@emotion/react';
import { Modal } from 'antd';
import type { ModalProps } from 'antd/lib/modal';
import { FormattedMessage } from 'react-intl';
import Loader from 'components/common/loader';
import { Explanation } from 'components/common/form-components';
import { legacyOnly, newDesignOnly, type Env0Theme } from 'constants/themes.constants';
import { typeStyle } from 'constants/typography';
import { PopupLayerContext, usePopupLayerZIndex } from 'components/common/popup-layer.context';

export type ModalSize = 'medium' | 'large' | 'content';

/**
 * Figma specs two widths. `content` means "no Figma size exists for this modal, so the call site's width
 * applies" - needed because eleven call sites are 800-1200 or a viewport percentage, and a data-table
 * modal at 600 is unusable. One named value stays auditable by design and greppable for APO-484; an
 * unbranched `width` would be neither.
 */
const SIZE_WIDTH: Record<ModalSize, number | undefined> = {
  medium: 440,
  large: 600,
  content: undefined
};

/**
 * `width` is an antd prop, not a style, so `legacyOnly` cannot reach it. Switch-off therefore keeps the
 * call site's own number - that is what holds legacy parity, and why `width` stays next to a named `size`
 * on the call sites the new design overrides. APO-484 deletes the branch, and every such `width` with it.
 */
const resolveModalWidth = (theme: Env0Theme, size: ModalSize, width: ModalProps['width']) =>
  theme.isNewDesign ? (SIZE_WIDTH[size] ?? width) : width;

export interface CommonModalProps extends ModalProps, Omit<ModalTitleProps, 'id'> {
  titleId?: string;
  isLoading?: boolean;
  size?: ModalSize;
}

const CommonModal: React.FunctionComponent<CommonModalProps> = ({
  isLoading,
  className,
  children,
  titleId,
  subtitleId,
  subtitleValues,
  titleValues,
  size = 'large',
  width,
  zIndex,
  ...props
}) => {
  const theme = useTheme();
  // The call site's zIndex switch-off, so the legacy DOM carries no inline z-index it did not before.
  const resolvedZIndex = usePopupLayerZIndex('modal', zIndex);

  // The provider sits outside the modal so a modal opened from the title or footer nests too.
  return (
    <PopupLayerContext.Provider value={resolvedZIndex}>
      <StyledModal
        className={`${className || ''} ${isLoading ? 'loading' : ''}`}
        width={resolveModalWidth(theme, size, width)}
        zIndex={resolvedZIndex}
        title={
          titleId && (
            <ModalTitle
              id={titleId}
              titleValues={titleValues}
              subtitleId={subtitleId}
              subtitleValues={subtitleValues}
            />
          )
        }
        {...props}>
        <div className="loading-overlay">
          <Loader />
        </div>
        {children}
      </StyledModal>
    </PopupLayerContext.Provider>
  );
};

interface ModalTitleProps {
  id: string;
  subtitleId?: string;
  subtitleValues?: Record<string, React.ReactNode | ((parts: string | ReactNode) => ReactNode)>;
  titleValues?: Record<string, React.ReactNode | ((parts: string | ReactNode) => ReactNode)>;
}
const ModalTitle: React.FunctionComponent<ModalTitleProps> = ({ id, subtitleId, subtitleValues, titleValues }) => {
  return (
    <>
      <Title>
        <FormattedMessage id={id} values={titleValues} />
      </Title>
      {subtitleId && (
        <Explanation>
          <FormattedMessage id={subtitleId} values={subtitleValues} />
        </Explanation>
      )}
    </>
  );
};

const Title = styled.div`
  ${legacyOnly(
    theme => css`
      color: ${theme.primaryBlack};
      font-size: 24px;
      font-weight: 500;
      margin-bottom: 19px;
      margin-top: 19px;
    `
  )}

  ${newDesignOnly(
    theme => css`
      ${typeStyle.headingH3}
      color: var(--text-heading);
      /* Only the gap to the subtitle. The header's own insets come from the bridge and newDesignChrome. */
      margin: 0 0 ${theme.spacing.xxxs};
    `
  )}
`;

/**
 * What the bridge cannot give.
 *
 * The header gap: antd draws `.ant-modal-header`'s bottom margin from `marginXS`, which it also uses for
 * the gap between footer buttons - and 8 is exactly Figma's button gap, so one token cannot carry both.
 * Figma's 18 is the header's own 12px bottom inset plus the 6px gap between the modal's sections.
 *
 * The close button: antd's box is `fontSize x lineHeight` = 22 and it derives both axes of the position
 * from one expression, `(modalHeaderHeight - modalCloseBtnSize) / 2` = 17/17. `modalCloseBtnSize` is
 * recomputed after the theme merge and is therefore inert (measured), so Figma's 32x32 box and its 16
 * inset cannot come from tokens. `top` alone happens to land on antd's own 17 - see below.
 *
 * `top` is 17 rather than Figma's 29 (APO-462 review). Measured against the rendered header, where the
 * heading's first line spans 20-46 (centre 33), the subtitle 50-70, and the block centre is 45:
 *
 * - 29 puts the icon centre on 45, the centre of the two-line block the Figma frame draws. With a
 *   single-line title - most of our modals - there is no second line to balance, and it reads as low.
 * - 17 puts it on 33, the heading's first line, in both cases. That also lands the glyph 25 from the top
 *   against 24 from the right, so it reads as a corner control.
 *
 * A fixed `top` centres the glyph on a 26px line, so a composed title whose first line is a different
 * height is off by a little: measured at +1 (cloud-resource's icon row, 21px) to -3 (notification-events,
 * 32px). Figma's 29 is 9 to 13 out on the same modals. Content padding is not a variable - it is antd's
 * 20/24 on every new-design modal, and the one call-site override left (popup-entity's 3em) is legacy.
 *
 * Box size and colour are Figma's and unchanged: the Icon button is 32x32 with `padding-xs` 8 around a
 * 16px glyph, in `--icon-primary` #0c0c0b.
 *
 * Emotion's class lands on `.ant-modal` itself, so these are descendant selectors: two classes against
 * antd's one, which needs no `!important` and no doubled class. This replaces the `!important` global
 * APO-462 deleted from `antd-wrapper.tsx`.
 */
const newDesignChrome = css`
  .ant-modal-header {
    margin-bottom: 18px;
  }

  .ant-modal-close {
    top: 17px;
    inset-inline-end: 16px;
    width: 32px;
    height: 32px;
  }
`;

/* Dark only: the backdrop token is gray-900-alpha over a gray-900-family page — the same colour the
   modal is filled with — and the modal shadow is 4%-alpha black, so a dark modal had no visible edge
   at all (UI-polish review; the backdrop value itself is raised with design as a token gap). Light
   modals keep Figma's borderless card. */
const darkModeEdge = css`
  .ant-modal-content {
    border: 1px solid var(--border-primary);
  }
`;

const StyledModal = styled(Modal)`
  ${newDesignOnly(() => newDesignChrome)}
  ${props => (props.theme.isNewDesign && props.theme.mode === 'dark' ? darkModeEdge : undefined)}

  .ant-modal {
    &-body {
      > .loading-overlay {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        opacity: 0;
        pointer-events: none;
        transition: all 420ms ease-in-out;
      }
      > :not(.loading-overlay) {
        transition: all 420ms ease-in-out;
      }
    }
    &-header > *,
    &-footer > * {
      transition: all 420ms ease-in-out;
    }
  }
  &.loading {
    .ant-modal {
      &-header,
      &-footer {
        > * {
          opacity: 0.13;
          pointer-events: none;
        }
      }
      &-body {
        > :not(.loading-overlay) {
          opacity: 0.13;
          pointer-events: none;
        }
        > .loading-overlay {
          pointer-events: all;
          opacity: 1;
        }
      }
    }
  }
`;

/**
 * For the four raw-antd `<Modal>` instances (`scim-reconcile`, `scim-configuration` x2,
 * `create-project-modal`). They swap `import { Modal } from 'antd'` for this and change nothing else.
 *
 * A bridge rather than a global `.ant-modal` rule in `antd-wrapper.tsx`: that would be design-system
 * drift with no owner and no cleanup trigger. And a bridge rather than nothing, because `CommonModal`
 * genuinely ADDS to what a raw call site renders - the loading-overlay node, the `titleId` composition
 * and the 420ms body transitions - so the two are not interchangeable.
 */
export const BridgedModal: React.FunctionComponent<CommonModalProps> = props => {
  const theme = useTheme();

  return theme.isNewDesign ? <CommonModal {...props} /> : <Modal {...props} />;
};

export const Bold = styled.span`
  font-weight: bold;
`;

export default CommonModal;
